import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ShoppingCart,
    IndianRupee,
    Calendar,
    Clock,
    Package,
    AlertCircle,
    XCircle,
    Truck,
    Navigation,
    CheckCircle,
    Layers,
    AlertTriangle,
    RefreshCw,
    ArrowRight,
    Eye,
    ChevronDown,
    Calendar as CalendarIcon
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Button from '../../../components/common/Button/Button';
import orderDashboardService from '../../../api/crm/orders/orderDashboardService';
import margDataService from '../../../api/crm/marg/margDataService';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import DateRangeFilter from '../../../components/common/DateRangeFilter';

const OrderDashboard = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const companyId = process.env.REACT_APP_MARG_COMPANY_ID || null;

    // ── Date Filter State ───────────────────────────────────────────────────
    const [selectedPeriod, setSelectedPeriod] = useState('all_time');
    const [customDateRange, setCustomDateRange] = useState({ startDate: '', endDate: '' });

    // Build filter object passed to the API
    const dashboardFilters = useMemo(() => {
        const f = { companyId: process.env.REACT_APP_MARG_COMPANY_ID || null };
        if (customDateRange.startDate || customDateRange.endDate) {
            if (customDateRange.startDate) f.startDate = customDateRange.startDate;
            if (customDateRange.endDate) f.endDate = customDateRange.endDate;
            f.dateRange = 'custom';
        } else {
            f.dateRange = selectedPeriod;
        }
        return f;
    }, [selectedPeriod, customDateRange]);

    // ── React Query Hooks ──────────────────────────────────────────────────
    
    // 1. Dashboard Overview (Recent Orders & basic stats)
    const { 
        data: overviewData, 
        isLoading: overviewLoading,
        isRefetching: overviewRefetching
    } = useQuery({
        queryKey: ['orderDashboard', 'overview', dashboardFilters],
        queryFn: () => orderDashboardService.getDashboardOverview(dashboardFilters),
        select: (result) => result.success ? (result.data?.data || result.data || {}) : {},
        refetchOnWindowFocus: false,
        staleTime: 5000
    });

    // 2. Orders Analytics (Totals & Revenue)
    const { 
        data: ordersAnalytics, 
        isLoading: ordersLoading,
        isRefetching: ordersRefetching
    } = useQuery({
        queryKey: ['orderDashboard', 'analytics', companyId, dashboardFilters],
        queryFn: () => margDataService.getOrdersAnalytics({ companyId, ...dashboardFilters }),
        select: (result) => result.success ? result : { totalOrders: 0, totalRevenue: 0 },
        refetchOnWindowFocus: false,
        staleTime: 5000
    });

    // 3. Inventory Analytics (Inventory + Batch stats)
    const { 
        data: inventoryAnalytics, 
        isLoading: inventoryLoading,
        isRefetching: inventoryRefetching
    } = useQuery({
        queryKey: ['orderDashboard', 'inventory', companyId, dashboardFilters],
        queryFn: () => margDataService.getInventoryAnalytics({ companyId, ...dashboardFilters }),
        select: (result) => result.success ? result : { 
            totalProducts: 0, lowStockItems: 0, outOfStockItems: 0,
            totalBatches: 0, activeBatches: 0, expiringSoon: 0, expiredBatches: 0
        },
        refetchOnWindowFocus: false,
        staleTime: 5000
    });

    const isRefreshing = overviewRefetching || ordersRefetching || inventoryRefetching;

    const handleRefresh = () => {
        queryClient.invalidateQueries({ queryKey: ['orderDashboard'] });
        toast.success('Refreshing dashboard...');
    };

    // Derived data for display
    const recentOrders = overviewData?.recentOrders || [];
    const orderOverview = {
        totalOrders: ordersAnalytics?.totalOrders || overviewData?.orderOverview?.totalOrders || 0,
        totalRevenue: ordersAnalytics?.totalRevenue || overviewData?.orderOverview?.totalRevenue || 0,
        totalDelivered: overviewData?.orderOverview?.totalDelivered || 0
    };

    const inventoryStatus = {
        totalProducts: inventoryAnalytics?.totalProducts || overviewData?.inventoryStatus?.totalProducts || 0,
        lowStockItems: inventoryAnalytics?.lowStockItems || overviewData?.inventoryStatus?.lowStockItems || 0,
        outOfStock: inventoryAnalytics?.outOfStockItems || overviewData?.inventoryStatus?.outOfStock || 0
    };

    const batchStatus = {
        totalBatches: inventoryAnalytics?.totalBatches || overviewData?.batchStatus?.totalBatches || 0,
        activeBatches: inventoryAnalytics?.activeBatches || overviewData?.batchStatus?.activeBatches || 0,
        expiringSoon: inventoryAnalytics?.expiringSoon || overviewData?.batchStatus?.expiringSoon || 0,
        expired: inventoryAnalytics?.expiredBatches || overviewData?.batchStatus?.expired || 0
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // KPI Card Component
    const KPICard = ({ title, value, icon: Icon, color, onClick, isLoading }) => (
        <div
            className={`bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-all duration-200 ${onClick ? 'cursor-pointer hover:border-gray-200' : ''}`}
            onClick={onClick}
        >
            <div className="flex items-center">
                <div className={`p-3 rounded-xl ${color} flex-shrink-0 mr-4`}>
                    <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-500 truncate">{title}</p>
                    {isLoading ? (
                        <div className="h-7 w-16 bg-gray-200 animate-pulse rounded mt-1"></div>
                    ) : (
                        <p className="text-xl font-bold text-gray-900 mt-1">{value}</p>
                    )}
                </div>
            </div>
        </div>
    );

    // Section Header Component
    const SectionHeader = ({ icon: Icon, title, color, linkText, linkTo }) => (
        <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
                <div className={`p-2 rounded-lg ${color}`}>
                    <Icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            </div>
            {linkText && linkTo && (
                <button
                    onClick={() => navigate(linkTo)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center space-x-1"
                >
                    <span>{linkText}</span>
                    <ArrowRight className="h-4 w-4" />
                </button>
            )}
        </div>
    );

    // Quick Link Card Component
    const QuickLinkCard = ({ title, description, icon: Icon, color, path }) => (
        <button
            onClick={() => navigate(path)}
            className="flex items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors w-full text-left group"
        >
            <div className={`p-3 rounded-xl ${color} flex-shrink-0 mr-4 group-hover:scale-105 transition-transform`}>
                <Icon className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">{title}</p>
                <p className="text-xs text-gray-500 truncate">{description}</p>
            </div>
            <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all" />
        </button>
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 !mt-0">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
                    <p className="text-gray-600 mt-1">Monitor orders, inventory, dispatch, and batch status at a glance</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {/* Period selector — hidden when a custom date range is active */}
                    {(!customDateRange.startDate && !customDateRange.endDate) && (
                        <select
                            value={selectedPeriod}
                            onChange={(e) => setSelectedPeriod(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                            <option value="all_time">All Time</option>
                            <option value="today">Today</option>
                            <option value="last_7_days">Last 7 days</option>
                            <option value="last_30_days">Last 30 days</option>
                            <option value="last_60_days">Last 60 days</option>
                            <option value="last_90_days">Last 90 days</option>
                        </select>
                    )}

                    <DateRangeFilter
                        startDate={customDateRange.startDate}
                        endDate={customDateRange.endDate}
                        onChange={(range) => setCustomDateRange(range)}
                        allowFutureDates={false}
                    />

                    {(customDateRange.startDate || customDateRange.endDate) && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setCustomDateRange({ startDate: '', endDate: '' });
                                setSelectedPeriod('last_30_days');
                            }}
                        >
                            Clear
                        </Button>
                    )}

                    <Button
                        variant="outline"
                        size="sm"
                        icon={<RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />}
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                    >
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Order Overview Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <SectionHeader
                    icon={ShoppingCart}
                    title="Overview"
                    color="bg-blue-500"
                    linkText="View All Orders"
                    linkTo="/order_management"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <KPICard
                        title="Orders"
                        value={orderOverview.totalOrders?.toLocaleString() || '0'}
                        icon={ShoppingCart}
                        color="bg-blue-500"
                        isLoading={ordersLoading || isRefreshing}
                    />
                    <KPICard
                        title="Revenue"
                        value={`₹${formatIndianAmount(orderOverview.totalRevenue || 0)}`}
                        icon={IndianRupee}
                        color="bg-emerald-500"
                        isLoading={ordersLoading || isRefreshing}
                    />
                    <KPICard
                        title="Total Delivered"
                        value={orderOverview.totalDelivered?.toLocaleString() || '0'}
                        icon={CheckCircle}
                        color="bg-cyan-500"
                        isLoading={overviewLoading || isRefreshing}
                    />
                </div>
            </div>

            {/* Inventory Status Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <SectionHeader
                    icon={Package}
                    title="Inventory"
                    color="bg-green-500"
                    linkText="Sync Inventory"
                    linkTo="/inventory_sync"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <KPICard
                        title="Products"
                        value={inventoryStatus.totalProducts?.toLocaleString() || '0'}
                        icon={Package}
                        color="bg-green-500"
                        isLoading={inventoryLoading || isRefreshing}
                    />
                    <KPICard
                        title="In Stock"
                        value={Math.max(0, (inventoryStatus.totalProducts || 0) - (inventoryStatus.lowStockItems || 0) - (inventoryStatus.outOfStock || 0)).toLocaleString()}
                        icon={CheckCircle}
                        color="bg-emerald-500"
                        isLoading={inventoryLoading || isRefreshing}
                    />
                    <KPICard
                        title="Low Stock"
                        value={inventoryStatus.lowStockItems?.toLocaleString() || '0'}
                        icon={AlertCircle}
                        color="bg-yellow-500"
                        isLoading={inventoryLoading || isRefreshing}
                    />
                    <KPICard
                        title="Out of Stock"
                        value={inventoryStatus.outOfStock?.toLocaleString() || '0'}
                        icon={XCircle}
                        color="bg-red-500"
                        isLoading={inventoryLoading || isRefreshing}
                    />
                </div>
            </div>

            {/* Batch & Expiry Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <SectionHeader
                    icon={Layers}
                    title="Batches"
                    color="bg-indigo-500"
                    linkText="Manage Batches"
                    linkTo="/batch_expiry"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <KPICard
                        title="Batches"
                        value={batchStatus.totalBatches?.toLocaleString() || '0'}
                        icon={Layers}
                        color="bg-blue-500"
                        isLoading={inventoryLoading || isRefreshing}
                    />
                    <KPICard
                        title="Active Batches"
                        value={batchStatus.activeBatches?.toLocaleString() || '0'}
                        icon={CheckCircle}
                        color="bg-green-500"
                        isLoading={inventoryLoading || isRefreshing}
                    />
                    <KPICard
                        title="Expiring Soon"
                        value={batchStatus.expiringSoon?.toLocaleString() || '0'}
                        icon={Clock}
                        color="bg-orange-500"
                        isLoading={inventoryLoading || isRefreshing}
                    />
                    <KPICard
                        title="Expired"
                        value={batchStatus.expired?.toLocaleString() || '0'}
                        icon={AlertTriangle}
                        color="bg-red-500"
                        isLoading={inventoryLoading || isRefreshing}
                    />
                </div>
            </div>

            {/* Bottom Section - Recent Orders & Quick Links */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Recent Orders */}
                <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <SectionHeader
                        icon={ShoppingCart}
                        title="Recent Orders"
                        color="bg-slate-600"
                        linkText="View All"
                        linkTo="/order_management"
                    />
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-4">Invoice ID</th>
                                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-4">Customer</th>
                                    <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-4">Amount</th>
                                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-4">Date</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {overviewLoading && !isRefreshing ? (
                                    // Table Loading Skeleton
                                    [...Array(5)].map((_, i) => (
                                        <tr key={`skeleton-${i}`} className="animate-pulse">
                                            <td className="py-4 px-4"><div className="h-4 bg-gray-100 rounded w-24"></div></td>
                                            <td className="py-4 px-4"><div className="h-4 bg-gray-100 rounded w-48"></div></td>
                                            <td className="py-4 px-4 text-right"><div className="h-4 bg-gray-100 rounded w-16 ml-auto"></div></td>
                                            <td className="py-4 px-4"><div className="h-4 bg-gray-100 rounded w-20"></div></td>
                                        </tr>
                                    ))
                                ) : recentOrders.length > 0 ? (
                                    recentOrders.map((order, index) => (
                                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                                            <td className="py-3 px-4">
                                                <span className="text-sm font-medium text-blue-600">{order.orderId || 'N/A'}</span>
                                            </td>
                                            <td className="py-3 px-4">
                                                <span className="text-sm text-gray-900">{order.customer || 'CASH'}</span>
                                            </td>
                                            <td className="py-3 px-4 text-right">
                                                <span className="text-sm font-semibold text-gray-900">₹{formatIndianAmount(order.amount || 0)}</span>
                                            </td>
                                            <td className="py-3 px-4">
                                                <span className="text-sm text-gray-600">{formatDate(order.date)}</span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="py-8 text-center text-gray-500">
                                            <ShoppingCart className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                                            <p>No recent orders</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Quick Links */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center space-x-2 mb-4">
                        <div className="p-2 rounded-lg bg-gray-600">
                            <ArrowRight className="h-5 w-5 text-white" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Quick Links</h3>
                    </div>
                    <div className="space-y-3">
                        <QuickLinkCard
                            title="Order Management"
                            description="Manage all customer orders"
                            icon={ShoppingCart}
                            color="bg-blue-500"
                            path="/order_management"
                        />
                        <QuickLinkCard
                            title="Inventory Sync"
                            description="Sync inventory from ERP"
                            icon={Package}
                            color="bg-green-500"
                            path="/inventory_sync"
                        />
                        <QuickLinkCard
                            title="Batch & Expiry"
                            description="Manage product batches"
                            icon={Layers}
                            color="bg-indigo-500"
                            path="/batch_expiry"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderDashboard;
